import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import axios from 'axios';
import routes from '../../utils/routes';
import useCurrentUser from '../../utils/hooks/useCurrentUser';
import { ListingType } from '../../structures/models/listings';

interface FavoriteHit {
  id: number,
  listingType: ListingType,
}

interface FavoriteProps {
  hit: FavoriteHit,

  // If true or false, the favorite status has already been calculated and we don't need to
  // check it. If undefined, the favorite status is unknown and we must load it in
  isInitiallyFavorited?: boolean,
}

const Favourite: React.FC<FavoriteProps> = ({ hit, isInitiallyFavorited }) => {
  const [isFavorited, setIsFavorited] = useState(!!isInitiallyFavorited);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useCurrentUser();

  const fireAnonUserSwal = () => {
    Swal.fire(
      'Error',
      `Please <a href="${routes.WEB_LOGIN}">log in</a> to favorite!`,
      'error',
    )
      .then(() => setLoading(false));
  };

  useEffect(() => {
    if (currentUser.isAuthenticated && typeof isInitiallyFavorited === 'undefined') {
      axios.get(`/search/isfavorite/${hit.id}`)
        .then((res) => {
          if (res.data.entries) {
            setIsFavorited(true);
          } else {
            setIsFavorited(false);
          }
        })
        .catch(() => {
        });
    }
  }, [currentUser, isInitiallyFavorited]);

  const favoriteChange = useCallback(() => {
    if (currentUser.isAuthenticated) {
      const endpoint = isFavorited
        ? routes.apiUnfavoritePost(hit.listingType, hit.id)
        : routes.apiFavoritePost(hit.listingType, hit.id);
      setLoading(true);

      axios.post(endpoint)
        .then(() => {
          const message = isFavorited
            ? 'Listing removed from favorites'
            : 'Listing added to favorites';
          Swal.fire(
            'Success!',
            message,
            'success',
          ).then(() => {
            setLoading(false);
            setIsFavorited(!isFavorited);
          });
        })
        .catch((error) => {
          if (error.response.status === 401) {
            fireAnonUserSwal();
          } else {
            Swal.fire(
              'Error',
              'Please try again.',
              'error',
            )
              .then(() => setLoading(false));
          }
        });
    } else {
      fireAnonUserSwal();
    }
  }, [hit, isFavorited, currentUser]);

  if (loading) {
    return (
      <span className="like-icon">
        <i className="fa fa-spinner fa-spin" />
      </span>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        favoriteChange();
      }}
      type="button"
      className="like-icon"
      id={`favourite-${hit.id}`}
      data-is-favourite={isFavorited}
      data-listing-id={hit.id}
    >
      {isFavorited ? <i className="fas fa-heart" /> : <i className="far fa-heart" />}
    </button>
  );
};

export default Favourite;
